import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  CHEAPEST,
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
} from '../../components/Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { useDefaultFeeType } from 'clientState/providers/SearchResponseProvider';
import { usePriceTypeFilterTimeMetric } from 'clientState/hooks/metrics';

export type FeeType = { id: string };
type FeeRequestContextType = [FeeType | null, (feeType: FeeType) => void];
const loadingContextValue: FeeRequestContextType = [null, () => {}];

const FeeRequestContext =
  createContext<FeeRequestContextType>(loadingContextValue);

export const useFeeType = (): FeeRequestContextType => {
  const [feeType, setFeeType] = useContext(FeeRequestContext);
  const { start } = usePriceTypeFilterTimeMetric();
  const defaultFeeType = useDefaultFeeType() || null;
  const selectedFeeType = feeType || defaultFeeType;
  if (!selectedFeeType) {
    return loadingContextValue;
  }
  const applyFeeType = (feeType: FeeType) => {
    start();
    setFeeType(feeType);
  };
  return [selectedFeeType, applyFeeType];
};

const parseFeeType = (feeType) => {
  const id = feeType?.id;
  return {
    id: id === CHEAPEST ? '0' : id,
  };
};
const isPrimeFeeType = ({ id }) =>
  [
    MEMBER_PRICE_POLICY_DISCOUNTED,
    MEMBER_PRICE_POLICY_UNDISCOUNTED,
    MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
  ].includes(id);

const FeeTypeProvider = ({ onChange, children }) => {
  const stateAndDispatch = useState<FeeType | null>(null);
  const [feeType] = stateAndDispatch;

  useEffect(() => {
    const parsedFeeType = parseFeeType(feeType);
    if (parsedFeeType.id && !isPrimeFeeType(parsedFeeType)) {
      onChange(parsedFeeType);
    }
  }, [feeType, onChange]);

  return (
    <FeeRequestContext.Provider value={stateAndDispatch}>
      {children}
    </FeeRequestContext.Provider>
  );
};

export default FeeTypeProvider;
